// AppShell — Layout-Komponente die Sidebar + Topbar + Main + RightRail + TickerTape
// zusammenstellt. Source-of-truth: mockups/03-shell/B-bolt-hero.html.
//
// Side-Effect-Hooks die HIER beim Mount laufen (zentral, nicht pro Page):
//   - useHyperliquidGlobal: WS für Mids/AssetCtx/Whales → hyperliquid-store
//   - useDataSync: Owner 60s / User 120s Poll von /cache/data → market-store
//   - useMacroHistory: Side-Effect — speichert pro Tag einen Macro-Snapshot
//   - useEveProfile: Mount-Effect lädt user-spezifisches EVE-Profile
//   - useEveMemory: Mount-Effect lädt Memories + RAS + Global-RAS (sonst
//     antwortet EVE wie ein generischer Chatbot ohne Erinnerung)

import { useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { useDataSync } from '@/hooks/use-data-sync';
import { useEveMemoryBootstrap } from '@/hooks/use-eve-memory-bootstrap';
import { useEveProfile } from '@/hooks/use-eve-profile';
import { useHyperliquidGlobal } from '@/hooks/use-hyperliquid-global';
import { useMacroHistory } from '@/hooks/use-macro-history';
import { useWhaleAlertsStore } from '@/stores/whale-alerts-store';
import type { WhaleAlert } from '@/types/hyperliquid';
import { EvePresenceDrawer } from '@/components/eve/eve-presence-drawer';
import { InsufficientModal } from '@/components/credits/insufficient-modal';
import { RightRail } from './right-rail';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';
import { TickerTape } from './ticker-tape';

export function AppShell() {
  // Whale-Alerts in den Store schreiben (Right-Rail + Markets-Marquee konsumieren).
  const pushWhale = useWhaleAlertsStore((s) => s.push);
  const handleWhale = useCallback((a: WhaleAlert) => pushWhale(a), [pushWhale]);

  // Live-Daten-WS mounten (Mids + AssetCtx + Whales). Setzt auch
  // hyperliquid-store.livePrices/liveMetrics für die Hyperliquid-Page.
  useHyperliquidGlobal({ onWhaleTrade: handleWhale });

  // Market-Cache Polling (BTC/ETH/SOL Live-Quote · DXY/VIX/SPX/GOLD · F&G).
  // Owner 60s, andere 120s — siehe useDataSync. Setzt market-store.fullData,
  // aus dem Right-Rail TabDaten und TickerTape direkt lesen.
  useDataSync();

  // Side-Effect: bei jedem fullData-Update einen täglichen Snapshot in
  // macro-history-store speichern. Idempotent — skippt wenn Tag schon da.
  useMacroHistory();

  // EVE-Profile + Memory beim Login laden — sonst klingt EVE wie ein
  // generischer Chatbot ohne Erinnerung. useEveMemoryBootstrap macht den
  // Mount-Trigger für loadMemories + loadRasRules + loadGlobalRasRules.
  useEveProfile();
  useEveMemoryBootstrap();

  return (
    <div className="relative flex min-h-dvh flex-col overflow-hidden text-neutral-100">
      {/* Background layer */}
      <div className="aurora">
        <div className="aurora__blob aurora__blob--1"></div>
        <div className="aurora__blob aurora__blob--2"></div>
        <div className="aurora__blob aurora__blob--3"></div>
      </div>
      <div className="aurora-grid"></div>
      <div className="aurora-vignette"></div>

      {/* Topbar above the 3-column body */}
      <Topbar />

      {/* 3-column body: Sidebar | Main | RightRail (3D) */}
      <div className="relative z-10 flex min-h-0 flex-1 overflow-hidden">
        <Sidebar />

        <main className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* WICHTIG: `relative` + `overflow-hidden`. Damit können Pages wie
              MarktPage `absolute inset-0` nutzen und garantiert exakt diesen
              Container füllen (Claude.ai-Style Layout-Anker). */}
          <div className="scroll-hidden relative min-h-0 flex-1 overflow-hidden">
            <Outlet />
          </div>
          <TickerTape />
        </main>

        <RightRail />
      </div>

      {/* Globaler EVE-Drawer — auf jeder Page präsent (außer /markt, dort
          ist EVE schon Vollbild). Per-Page eigener Chat-Verlauf. */}
      <EvePresenceDrawer />

      {/* Globales Insufficient-Credits-Modal — lauscht auf eve:insufficient-credits
          (von eve-runtime bei HTTP 402). Einmal hier gemountet. */}
      <InsufficientModal />
    </div>
  );
}
